import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  FiPlus,
  FiHash,
  FiMessageSquare,
  FiEdit2,
  FiTrash2,
  FiChevronDown,
  FiChevronUp,
  FiRefreshCw,
} from "react-icons/fi";
import { useModal } from "../../../globals/imports";
import { fetchMyChannels } from "../../../redux/slices/channelItemsSlice";
import EmptyState from "./EmptyState";
import { SkeletonList } from "./Skeleton";
import {
  setCreateChannelItems,
  setCreateChannelField,
  createClearChannel,
} from "../../../redux/slices/createChannelSlice";
import {
  setChannelIdToDelete,
  setChannelNameToDelete,
} from "../../../redux/slices/deleteChannelSlice";
import TopicsTab from "../../Channel/Tabs/TopicsTab";

const VisibilityBadge = ({ v }) => (
  <span className="rounded-full border border-theme-chatDivider px-2 py-0.5 text-[11px] font-medium text-theme-placeholder">
    {v || "anyone"}
  </span>
);

const ChannelsPanel = () => {
  const dispatch = useDispatch();
  const channels = useSelector((s) => s.channelItems.userChannels) || [];
  const loading = useSelector((s) => s.channelItems.loading);
  const { handleOpenModal } = useModal();
  const [expanded, setExpanded] = useState(null);

  const refresh = () => dispatch(fetchMyChannels());

  useEffect(() => {
    refresh();
    // refresh when returning to the tab (after a modal create/edit/delete)
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const handleCreate = () => {
    dispatch(createClearChannel());
    handleOpenModal("modalChannelOpen");
  };

  const handleEdit = (channel) => {
    dispatch(setCreateChannelItems({ ...channel }));
    dispatch(setCreateChannelField({ field: "isEdit", value: true }));
    setTimeout(() => handleOpenModal("modalChannelOpen"), 300);
  };

  const handleDelete = (channel) => {
    dispatch(setChannelIdToDelete(channel._id));
    dispatch(setChannelNameToDelete(channel.name));
    handleOpenModal("modalDeleteChannelOpen");
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-7 sm:px-6 lg:px-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-theme-secondaryText">
            Channels
          </h1>
          <p className="mt-1 text-sm text-theme-placeholder">
            The spaces inside your community. Each channel holds topics &amp;
            chat.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refresh}
            title="Refresh"
            className="rounded-lg border border-theme-chatDivider p-2 text-theme-primaryText transition-colors hover:bg-theme-tertiaryBackground"
          >
            <FiRefreshCw size={15} />
          </button>
          <button
            onClick={handleCreate}
            className="flex items-center gap-2 rounded-lg bg-theme-accent px-4 py-2 text-sm font-semibold text-theme-accentText hover:bg-theme-accentHover"
          >
            <FiPlus size={16} /> New channel
          </button>
        </div>
      </div>

      {loading && channels.length === 0 ? (
        <SkeletonList rows={5} />
      ) : channels.length === 0 ? (
        <EmptyState
          icon={FiHash}
          title="No channels yet"
          subtitle="Create your first channel, or let Channels AI draft a starter set for you."
          action={
            <button
              onClick={handleCreate}
              className="rounded-lg bg-theme-accent px-4 py-2 text-sm font-semibold text-theme-accentText hover:bg-theme-accentHover"
            >
              Create a channel
            </button>
          }
        />
      ) : (
        <div className="flex flex-col gap-3">
          {channels.map((c) => {
            const isOpen = expanded === c._id;
            return (
              <div
                key={c._id}
                className="rounded-2xl border border-theme-chatDivider bg-theme-secondaryBackground"
              >
                <div className="flex items-center gap-3 p-4">
                  {c.logo ? (
                    <img
                      src={c.logo}
                      alt={c.name}
                      className="h-10 w-10 flex-shrink-0 rounded-lg object-cover"
                    />
                  ) : (
                    <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-theme-tertiaryBackground text-theme-accent">
                      <FiHash size={18} />
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-sm font-semibold text-theme-secondaryText">
                        {c.name}
                      </p>
                      <VisibilityBadge v={c.visibility} />
                    </div>
                    <div className="mt-0.5 flex items-center gap-1.5 text-xs text-theme-placeholder">
                      <FiMessageSquare size={12} />
                      {(c.topics?.length || 0)} topic
                      {(c.topics?.length || 0) === 1 ? "" : "s"}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleEdit(c)}
                      title="Edit channel"
                      className="rounded-md p-2 text-theme-placeholder transition-colors hover:bg-theme-tertiaryBackground hover:text-theme-secondaryText"
                    >
                      <FiEdit2 size={15} />
                    </button>
                    <button
                      onClick={() => handleDelete(c)}
                      title="Delete channel"
                      className="rounded-md p-2 text-theme-placeholder transition-colors hover:bg-theme-tertiaryBackground hover:text-theme-error"
                    >
                      <FiTrash2 size={15} />
                    </button>
                    <button
                      onClick={() => setExpanded(isOpen ? null : c._id)}
                      title="Manage topics"
                      className="flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-semibold text-theme-primaryText transition-colors hover:bg-theme-tertiaryBackground"
                    >
                      Topics
                      {isOpen ? (
                        <FiChevronUp size={14} />
                      ) : (
                        <FiChevronDown size={14} />
                      )}
                    </button>
                  </div>
                </div>
                {isOpen && (
                  <div className="border-t border-theme-chatDivider px-4 py-3">
                    <TopicsTab channelId={c._id} isOwner={true} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ChannelsPanel;
